import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector from '../LanguageSelector';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <header className="bg-background-light py-4 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="w-36 h-36 relative">
              <img 
                src="/logofooter.png" 
                alt="De Blauwe Vogel" 
                className="absolute inset-0 w-full h-full object-contain"
                onError={(e) => {
                  // Fallback if image is not available
                  const target = e.target as HTMLImageElement;
                  target.src = "/logo-placeholder.svg";
                }}
              />
            </div>
          </Link>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <div className="mr-4">
              <LanguageSelector />
            </div>
            <button
              onClick={toggleMenu}
              className="text-secondary p-2 focus:outline-none"
            >
              {isOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1 items-center">
            <NavLink href="/" label="Home" />
            <NavLinkWithDropdown 
              href="/reisaanbod" 
              label="Reisaanbod" 
              items={[
                { label: "Nieuw!", href: "/reisaanbod/nieuw" },
                { label: "Begeleide rondreizen", href: "/reisaanbod/begeleide-rondreizen" },
                { label: "Begeleide busreizen", href: "/reisaanbod/begeleide-busreizen" },
                { label: "Hotels & Strand", href: "/reisaanbod/hotels-strand" },
                { label: "Citytrips", href: "/reisaanbod/citytrips" },
                { label: "Cruises", href: "/reisaanbod/cruises" },
                { label: "Riviercruises", href: "/reisaanbod/riviercruises" },
                { label: "Kortbij", href: "/reisaanbod/kortbij" },
                { label: "Reizen voor singles", href: "/reisaanbod/singles" },
                { label: "Volledig aanbod", href: "/reisaanbod" }
              ]} 
            />
            <NavLink href="/themadagen" label="Themadagen" />
            <NavLinkWithDropdown 
              href="/praktische-info" 
              label="Praktische info" 
              items={[
                { label: "Reserveren?", href: "/praktische-info/reserveren" },
                { label: "Verzekering", href: "/praktische-info/verzekering" },
                { label: "Visum USA", href: "/praktische-info/visum-usa" },
                { label: "FAQ", href: "/praktische-info/faq" },
                { label: "Wie is wie", href: "/praktische-info/wie-is-wie" }
              ]} 
            />
            <NavLinkWithDropdown 
              href="/contact" 
              label="Contact" 
              items={[
                { label: "Contactformulier", href: "/contact" },
                { label: "Inschrijven nieuwsbrief", href: "/nieuwsbrief" },
                { label: "Openingsuren", href: "/contact/openingsuren" }
              ]} 
            />
            <div className="ml-2">
              <LanguageSelector />
            </div>
          </nav>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 bg-white rounded-lg shadow-lg p-4">
            <nav className="flex flex-col space-y-2">
              <MobileNavLink href="/" label="Home" />
              <MobileNavLinkWithDropdown 
                href="/reisaanbod" 
                label="Reisaanbod" 
                items={[
                  { label: "Nieuw!", href: "/reisaanbod/nieuw" },
                  { label: "Begeleide rondreizen", href: "/reisaanbod/begeleide-rondreizen" },
                  { label: "Begeleide busreizen", href: "/reisaanbod/begeleide-busreizen" },
                  { label: "Hotels & Strand", href: "/reisaanbod/hotels-strand" },
                  { label: "Citytrips", href: "/reisaanbod/citytrips" },
                  { label: "Cruises", href: "/reisaanbod/cruises" },
                  { label: "Riviercruises", href: "/reisaanbod/riviercruises" },
                  { label: "Kortbij", href: "/reisaanbod/kortbij" },
                  { label: "Reizen voor singles", href: "/reisaanbod/singles" },
                  { label: "Volledig aanbod", href: "/reisaanbod" }
                ]} 
              />
              <MobileNavLink href="/themadagen" label="Themadagen" />
              <MobileNavLinkWithDropdown 
                href="/praktische-info" 
                label="Praktische info" 
                items={[
                  { label: "Reserveren?", href: "/praktische-info/reserveren" },
                  { label: "Verzekering", href: "/praktische-info/verzekering" },
                  { label: "Visum USA", href: "/praktische-info/visum-usa" },
                  { label: "FAQ", href: "/praktische-info/faq" },
                  { label: "Wie is wie", href: "/praktische-info/wie-is-wie" }
                ]} 
              />
              <MobileNavLinkWithDropdown 
                href="/contact" 
                label="Contact" 
                items={[
                  { label: "Contactformulier", href: "/contact" },
                  { label: "Inschrijven nieuwsbrief", href: "/nieuwsbrief" },
                  { label: "Openingsuren", href: "/contact/openingsuren" }
                ]} 
              />
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

type NavLinkProps = {
  href: string;
  label: string;
};

const NavLink: React.FC<NavLinkProps> = ({ href, label }) => {
  return (
    <Link
      to={href}
      className="px-3 py-2 text-primary-dark hover:text-primary transition duration-300 font-medium rounded-md hover:bg-gray-100"
    >
      {label}
    </Link>
  );
};

type NavLinkWithDropdownProps = {
  href: string;
  label: string;
  items: { label: string; href: string }[];
};

const NavLinkWithDropdown: React.FC<NavLinkWithDropdownProps> = ({
  href,
  label,
  items,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative" onMouseLeave={() => setIsOpen(false)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        className="flex items-center px-3 py-2 text-primary-dark hover:text-primary transition duration-300 font-medium rounded-md hover:bg-gray-100"
      >
        {label}
        <svg
          className="ml-1 h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white z-10">
          <div className="py-1">
            {items.map((item, index) => (
              <Link
                key={index}
                to={item.href}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileNavLink: React.FC<NavLinkProps> = ({ href, label }) => {
  return (
    <Link
      to={href}
      className="block px-3 py-2 text-primary-dark font-medium rounded-md hover:bg-gray-100"
    >
      {label}
    </Link>
  );
};

const MobileNavLinkWithDropdown: React.FC<NavLinkWithDropdownProps> = ({
  href,
  label,
  items,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-primary-dark font-medium rounded-md hover:bg-gray-100"
      >
        {label}
        <svg
          className={`h-5 w-5 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="pl-4 mt-1">
          {items.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header; 